import React, { useCallback, useState } from 'react';
import { Printer } from 'lucide-react';

import {
    getCompanySourceBadgeLabel,
    getCompanySourceColors,
} from 'components/receiving/components/AllocatedReceiving/AllocationBreakdown/companySourceAllocationConfig';
import { Button } from 'shadcn/components/ui/Button';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import dayjs from 'utils/dayjsConfig';

import { isValidCaseLabelCount, sanitizeCaseLabelCountInput } from './printLabelsUtils';

function formatBbd(expiresOn) {
    if (!expiresOn) {
        return 'BBD N/A';
    }

    const formatted = dayjs(expiresOn).format('MM/DD/YYYY');
    return formatted === 'Invalid Date' ? 'BBD N/A' : `BBD ${formatted}`;
}

function renderSourceLabel({ badgeColor, badgeLabel, isFbmMode, lot, partnerDisplayName }) {
    if (isFbmMode) {
        return <span className="text-sm font-semibold">{partnerDisplayName ?? 'Partner'}</span>;
    }

    if (badgeLabel) {
        return (
            <span
                className="inline-flex items-center justify-center rounded px-1.5 py-0.5 text-xs font-semibold text-white"
                style={{ backgroundColor: badgeColor }}
            >
                {badgeLabel}
            </span>
        );
    }

    if (lot.companySource) {
        return <span className="text-sm font-semibold">{lot.companySource.displayName}</span>;
    }

    return null;
}

function handlePrintCaseTags() {
    // TODO: print case tags for this lot
}

/**
 * @param {Object} props
 * @param {string} props.fieldIdPrefix
 * @param {boolean} [props.isFbmMode]
 * @param {{ id: string, caseCount: number, expiresOn: string | null, companySource: { id: number, code: string, displayName: string } | null }} props.lot
 * @param {number} props.lotIndex
 * @param {string | null} [props.partnerDisplayName]
 */
function PrintPalletLotRow({ fieldIdPrefix, isFbmMode = false, lot, lotIndex, partnerDisplayName = null }) {
    const [caseLabelCount, setCaseLabelCount] = useState(() => lot.caseCount);
    const caseLabelsInputId = `${fieldIdPrefix}-case-labels`;
    const casesLabel = lot.caseCount === 1 ? 'case' : 'cases';
    const badgeLabel = lot.companySource ? getCompanySourceBadgeLabel(lot.companySource.code) : null;
    const { badgeColor } = lot.companySource ? getCompanySourceColors(lot.companySource.code) : { badgeColor: null };

    const handleCaseLabelCountChange = useCallback(
        (event) => {
            setCaseLabelCount(sanitizeCaseLabelCountInput(event.target.value, lot.caseCount));
        },
        [lot.caseCount],
    );

    const canPrint = isValidCaseLabelCount(caseLabelCount, lot.caseCount);

    return (
        <div className="flex items-center justify-between gap-4 border-b border-border/60 py-2 last:border-b-0">
            <div className="flex min-w-0 flex-wrap items-center gap-3">
                <span className="text-xs text-muted-foreground">Lot {lotIndex + 1}</span>
                {renderSourceLabel({ badgeColor, badgeLabel, isFbmMode, lot, partnerDisplayName })}
                <span className="text-sm font-semibold">
                    {lot.caseCount} {casesLabel}
                </span>
                <span className="text-xs text-muted-foreground">{formatBbd(lot.expiresOn)}</span>
            </div>
            <div className="flex shrink-0 items-end gap-2">
                <div className="flex flex-col gap-1">
                    <Label className="text-xs leading-none text-muted-foreground" htmlFor={caseLabelsInputId}>
                        Case labels
                    </Label>
                    <Input
                        id={caseLabelsInputId}
                        className="h-8 w-20 text-sm"
                        min={1}
                        step={1}
                        type="number"
                        value={caseLabelCount}
                        onChange={handleCaseLabelCountChange}
                    />
                </div>
                <Button
                    className="h-8 shrink-0"
                    disabled={!canPrint}
                    size="sm"
                    type="button"
                    variant="outline"
                    onClick={handlePrintCaseTags}
                >
                    <Printer aria-hidden className="size-4" />
                    Print Case Tags
                </Button>
            </div>
        </div>
    );
}

export { PrintPalletLotRow, formatBbd, handlePrintCaseTags };
